import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import PageHero from '../../../components/sections/PageHero';
import PageTrustBar from '../../../components/sections/PageTrustBar';
import PageCollections from '../../../components/sections/PageCollections';
import PageAIAnswer from '../../../components/sections/PageAIAnswer';
import PageProcess from '../../../components/sections/PageProcess';
import PageWhyUs from '../../../components/sections/PageWhyUs';
import PageServiceArea from '../../../components/sections/PageServiceArea';
import PageTestimonials from '../../../components/sections/PageTestimonials';
import PageFAQ from '../../../components/sections/PageFAQ';
import PageFinalCTA from '../../../components/sections/PageFinalCTA';
import {
  FORM_URL, MAP_EMBED, SERVICE_AREAS, SERVICE_ADDRESS, SERVICE_HOURS, SERVICE_PHONE, TESTIMONIALS,
} from '../../../mocks/siteData';

const trustItems = [
  { icon: 'ri-medal-line', label: 'Authentic Antiques' },
  { icon: 'ri-search-eye-line', label: 'Carefully Sourced' },
  { icon: 'ri-history-line', label: 'Genuine Vintage Pieces' },
  { icon: 'ri-sparkling-line', label: 'One-of-a-Kind Only' },
  { icon: 'ri-refresh-line', label: 'New Finds Weekly' },
];

const subcategories = [
  {
    title: 'Vintage Décor & Curiosities',
    description: 'Wall art, mirrors, lamps, clocks, and decorative objects from bygone eras — each with its own story.',
    href: '/collections/antiques',
    tag: 'Featured',
    imageSrc: 'https://public.readdy.ai/ai/img_res/01729f565e842aa35ad73c848cb560d7.jpg',
  },
  {
    title: 'Collectibles & Rarities',
    description: 'Porcelain, pottery, glassware, figurines, and rare finds that collectors and enthusiasts will treasure.',
    href: '/collections/antiques',
    tag: 'Popular',
    imageSrc: 'https://readdy.ai/api/search-image?query=beautiful%20antique%20collectibles%20porcelain%20vases%20ceramic%20figurines%20vintage%20glassware%20silver%20items%20arranged%20display%20case%20warm%20lighting%20editorial%20boutique%20photography%20rare%20items&width=800&height=600&seq=ye-ant-collect&orientation=landscape',
  },
  {
    title: 'Rustic & Statement Pieces',
    description: 'Bold, characterful items — wrought iron, reclaimed wood, architectural salvage, and folk art.',
    href: '/collections/antiques',
    tag: 'New Arrivals',
    imageSrc: 'https://readdy.ai/api/search-image?query=rustic%20antique%20statement%20pieces%20wrought%20iron%20candle%20holders%20old%20wooden%20crates%20vintage%20metal%20signs%20folk%20art%20objects%20warm%20editorial%20photography%20boutique%20display&width=800&height=600&seq=ye-ant-rustic&orientation=landscape',
  },
];

const aiAnswers = [
  {
    q: 'What antiques does Yarnell Emporium carry?',
    a: 'Yarnell Emporium carries a rotating selection of genuine antiques including vintage décor, porcelain collectibles, glassware, architectural pieces, folk art, and rare curiosities — all carefully sourced for quality and character.',
  },
  {
    q: 'Are the antiques at Yarnell Emporium authentic?',
    a: 'Yes. Every antique and vintage item is carefully selected for authenticity and quality. The store avoids reproductions in favor of genuine pieces with real history, age, and provenance wherever possible.',
  },
  {
    q: 'Where are the antiques sourced from?',
    a: 'Items are sourced from estate sales, private collections, and specialty dealers throughout Arizona and the Southwest. This ensures a constantly rotating inventory of genuine antiques that can\'t be found elsewhere.',
  },
  {
    q: 'What makes antique shopping at Yarnell Emporium unique?',
    a: 'Unlike antique malls with dozens of vendors, Yarnell Emporium is a curated single-vision boutique. Every piece is chosen with intention, creating a cohesive and elevated shopping experience in a welcoming boutique environment.',
  },
];

const processSteps = [
  { number: '01', title: 'Browse the Collection', description: 'Explore our curated antique displays — each item is thoughtfully arranged to help you discover pieces that speak to you.' },
  { number: '02', title: 'Discover the Story', description: 'Every antique has history. Ask our team about the origin, age, and character of any piece that catches your eye.' },
  { number: '03', title: 'Evaluate & Envision', description: 'Consider how the piece fits your space, collection, or as a gift. We\'re happy to help with styling and décor advice.' },
  { number: '04', title: 'Make It Yours', description: 'Purchase your find knowing it\'s genuinely one-of-a-kind. Pieces sell quickly — don\'t wait on that special find.' },
];

const whyUsItems = [
  { icon: 'ri-verified-badge-line', title: 'Authentic, Not Reproduction', description: 'We prioritize genuine antiques and vintage pieces with real provenance over mass-produced imitations.' },
  { icon: 'ri-sparkling-2-line', title: 'Curated Single-Vision Collection', description: 'Unlike multi-vendor antique malls, our collection is curated with a consistent eye for quality and character.' },
  { icon: 'ri-refresh-line', title: 'Rotating Inventory', description: 'New antiques arrive regularly, meaning every visit reveals fresh discoveries. No two visits are the same.' },
  { icon: 'ri-user-heart-line', title: 'Knowledgeable & Passionate Team', description: 'Our team genuinely loves antiques. We\'re here to help you find exactly what you\'re looking for.' },
  { icon: 'ri-map-pin-line', title: 'Arizona\'s Hidden Antique Gem', description: 'A destination shop for antique lovers from Prescott, Wickenburg, Phoenix, and across Arizona.' },
  { icon: 'ri-price-tag-line', title: 'Fair, Transparent Pricing', description: 'Every item is priced fairly and transparently — no haggling, no confusion, just honest boutique pricing.' },
];

const faqItems = [
  { q: 'Where can I buy antiques near Prescott or Wickenburg, AZ?', a: 'Yarnell Emporium in Yarnell, AZ is conveniently located between Prescott and Wickenburg on Hwy 89. It\'s a popular antique destination for shoppers from across central Arizona, offering genuine vintage finds in a boutique setting.' },
  { q: 'Do you have vintage collectibles for sale?', a: 'Yes — Yarnell Emporium carries a rotating selection of collectibles including porcelain, glassware, figurines, and rarities. Inventory changes frequently, so each visit offers new and unique pieces for collectors and enthusiasts.' },
  { q: 'Are antiques expensive at Yarnell Emporium?', a: 'Yarnell Emporium offers a range of antiques at fair boutique prices. Whether you\'re looking for an affordable vintage accent or a statement investment piece, there\'s something for every budget in the collection.' },
  { q: 'How do I know if an antique is genuine?', a: 'Our team carefully sources authentic antiques and vintage items. We\'re happy to share what we know about any piece\'s history and age. Genuine quality and character are our standards for every item we carry.' },
  { q: 'Can I call ahead to ask about specific antiques?', a: 'Absolutely. Call us at 928.427-9593 any day between 9am–5pm. While we can\'t guarantee specific pieces will still be available, we\'re happy to describe current inventory and help plan your visit.' },
];

export default function AntiquesPage() {
  return (
    <div className="font-sans">
      <Navbar />
      <main>
        <PageHero
          badge="Antiques & Vintage · Yarnell, AZ"
          headline="Antiques & Vintage Finds"
          headlineItalic="in Yarnell, AZ"
          subheadline="Explore rare, timeless pieces with history and character. Genuine antiques, vintage collectibles, and one-of-a-kind curiosities — all curated in our boutique."
          urgency="Rotating inventory — rare pieces sell fast"
          ctas={[
            { label: 'Visit the Store', href: 'https://maps.google.com/?q=22899+S+Hwy+89+Yarnell+AZ+85362', external: true, primary: true },
            { label: 'Get Directions', href: 'https://maps.google.com/?q=22899+S+Hwy+89+Yarnell+AZ+85362', external: true },
          ]}
          imageSrc="https://readdy.ai/api/search-image?query=antique%20shop%20interior%20vintage%20collectibles%20porcelain%20brass%20ornate%20frames%20antique%20display%20cases%20warm%20moody%20lighting%20cinematic%20editorial%20photography%20high%20end%20boutique%20antique%20store&width=1920&height=1080&seq=ye-hero-antiques&orientation=landscape"
        />
        <PageTrustBar items={trustItems} />
        <PageCollections
          heading="Our Antique Collections"
          subheading="Curated Vintage Finds"
          intro="Every antique in our store is hand-selected for quality, character, and authenticity. Browse our current categories below."
          items={subcategories}
        />
        <PageAIAnswer
          heading="Antiques at Yarnell Emporium"
          subheading="About Our Collection"
          items={aiAnswers}
        />
        <PageProcess
          heading="The Antique Shopping Experience"
          subheading="How It Works"
          intro="Discovering the perfect antique is a journey. Here\'s what to expect when you visit Yarnell Emporium."
          steps={processSteps}
        />
        <PageWhyUs
          heading="Why Shop Antiques Here"
          subheading="Our Commitment"
          intro="We\'re antique enthusiasts ourselves. That passion shows in every piece we select and every customer we serve."
          items={whyUsItems}
          imageSrc="https://readdy.ai/api/search-image?query=antique%20shop%20display%20vintage%20items%20warm%20soft%20lighting%20carefully%20arranged%20collectibles%20rustic%20wood%20shelves%20intimate%20boutique%20atmosphere%20editorial%20photography&width=600&height=800&seq=ye-why-antiques&orientation=portrait"
        />
        <PageServiceArea
          heading="Antique Shopping Near Prescott & Phoenix"
          subheading="Serving Arizona"
          intro="Yarnell Emporium is a destination antique shop drawing visitors from across Arizona. Located on scenic Hwy 89 in Yarnell, AZ."
          areas={SERVICE_AREAS}
          address={SERVICE_ADDRESS}
          hours={SERVICE_HOURS}
          phone={SERVICE_PHONE}
          mapEmbed={MAP_EMBED}
        />
        <PageTestimonials
          heading="Antique Lovers Love Us"
          subheading="Customer Reviews"
          items={TESTIMONIALS}
        />
        <PageFAQ
          heading="Antique Buying Questions"
          subheading="FAQ"
          items={faqItems}
        />
        <PageFinalCTA
          heading="Discover Rare Antiques"
          headingItalic="You Won't Find Elsewhere"
          subheading="Visit Yarnell Emporium for a curated selection of genuine antiques, vintage collectibles, and statement pieces. Open daily 9am–5pm."
          urgency="Rare pieces — inventory rotates weekly"
          formUrl={FORM_URL}
          imageSrc="https://readdy.ai/api/search-image?query=antique%20shop%20interior%20warm%20golden%20evening%20light%20dramatic%20display%20vintage%20items%20rustic%20wooden%20furniture%20atmospheric%20editorial%20photography%20boutique&width=1920&height=1080&seq=ye-cta-antiques&orientation=landscape"
        />
      </main>
      <Footer />
    </div>
  );
}